/**
 * De onde vem cada lado de cada confronto do quadro (E91.F3, GUS-51 RF07).
 *
 * O QA transversal de 2026-09-12 mediu (UXD-07) que o cabeçalho "Declarado no
 * protocolo × Apurado no documento" atribuía os valores à fonte errada: no CPF
 * o primeiro lado é o CADASTRO do caso e o segundo é o protocolo; na custódia
 * interna é o registro de recebimento contra o arquivo armazenado. Quem lê a
 * fonte errada corrige o dado errado.
 *
 * Catálogo do lado da tela, por chave do item — a chave já é a identidade do
 * item (é assim que os rótulos funcionam), e o contrato `ItemVerificacao` não
 * ganha campo. A paridade com o motor é prendida em teste: toda chave do
 * catálogo de rótulos tem fonte, e as chaves batem com `ITEM_ROTULO` do
 * `laudo_eletronico_texto.py` (o teste lê o fonte Python).
 *
 * `referencia` é a fonte do valor esperado (`esperado`); `comparado`, a do
 * valor apurado (`obtido`). Item de provedor externo ou de presença tem só o
 * lado consultado — `referencia` fica `null`, e a célula não inventa o outro.
 *
 * Nem todo `esperado` vem de um documento (E91.F3.C01, F-E91.F3-01): nas datas
 * do arquivo o motor confronta o arquivo consigo mesmo ("criação anterior ou
 * igual à modificação") e no formato das datas confronta as âncoras entre si
 * ("uma leitura sustentada por todas as âncoras"). Esses lados são REGRA de
 * consistência, e o catálogo diz isso — rotulá-los de "Protocolo" mandava a
 * pessoa corrigir o dado errado, e deixá-los sem fonte caía no rótulo genérico.
 */

export type FonteDoConfronto =
  | 'protocolo'
  | 'regra_de_consistencia'
  | 'resolucao_das_datas'
  | 'arquivo_recebido'
  | 'arquivo'
  | 'arquivo_armazenado'
  | 'registro_de_recebimento'
  | 'cadastro_do_caso'
  | 'texto_do_documento'
  | 'comprovante'
  | 'consulta_cadastral'
  | 'consulta_de_localizacao'
  | 'consulta_de_rede';

export interface FontesDoItem {
  /** A fonte do valor esperado; `null` quando o item só tem o lado consultado. */
  referencia: FonteDoConfronto | null;
  /** A fonte do valor apurado. */
  comparado: FonteDoConfronto;
}

const F = (referencia: FonteDoConfronto | null, comparado: FonteDoConfronto): FontesDoItem => ({
  referencia,
  comparado,
});

/** Chave do item do motor → fontes. Mesmas chaves de `quadro.itens` e de `ITEM_ROTULO`. */
export const FONTES_POR_ITEM: Readonly<Record<string, FontesDoItem>> = {
  // Integridade (exame_eletronico_integridade.py): o hash declarado no
  // protocolo contra o recalculado sobre o arquivo recebido; o hash do registro
  // de recebimento contra o arquivo que ficou armazenado.
  hash_ccb: F('protocolo', 'arquivo_recebido'),
  custodia_interna: F('registro_de_recebimento', 'arquivo_armazenado'),
  // Identidade (exame_eletronico_identidade.py): o CPF esperado é o do CASO;
  // o nome do protocolo é procurado nos textos do caso; os demais são presença
  // no protocolo.
  cpf_signatario: F('cadastro_do_caso', 'protocolo'),
  nome_signatario: F('cadastro_do_caso', 'protocolo'),
  envelope_id: F(null, 'protocolo'),
  assinatura_id: F(null, 'protocolo'),
  sessao_biometrica: F(null, 'protocolo'),
  // E92.F1: a modalidade declarada no protocolo contra a apurada pela regra do exame.
  tipo_assinatura: F('protocolo', 'regra_de_consistencia'),
  // E101.F3: a página de assinaturas e a captura da biometria, confirmadas no protocolo; o
  // identificador das páginas vem da leitura confirmada de cada documento.
  vinculo_da_biometria: F('comprovante', 'comprovante'),
  vinculo_do_envelope: F('comprovante', 'texto_do_documento'),
  intervalo_da_biometria: F('regra_de_consistencia', 'comprovante'),
  // Provedores externos (exame_eletronico_cluster.py, _ip.py): só o lado consultado.
  cluster_cadastral: F(null, 'consulta_cadastral'),
  geocodificacao: F(null, 'consulta_de_localizacao'),
  classificacao_ip: F(null, 'consulta_de_rede'),
  // Assinatura embutida (exame_eletronico_assinatura_digital.py): o que o
  // próprio arquivo carrega; o titular é conferido contra o CPF do caso.
  assinatura_digital_presente: F(null, 'arquivo'),
  assinatura_digital_integridade: F(null, 'arquivo'),
  assinatura_digital_abrangencia: F(null, 'arquivo'),
  assinatura_digital_cadeia: F(null, 'arquivo'),
  assinatura_digital_revogacao: F(null, 'arquivo'),
  assinatura_digital_signatario: F('cadastro_do_caso', 'arquivo'),
  assinatura_digital_carimbo: F(null, 'arquivo'),
  // E157.F1 (ADR-0107 §2.5): o que o texto do documento mostra, contra o que o arquivo carrega.
  assinatura_digital_aparencia: F('texto_do_documento', 'arquivo'),
  // Metadados (exame_eletronico_metadados.py): o instante do ato registrado no
  // protocolo contra o que o arquivo declara de si — menos nas datas, em que o
  // arquivo é confrontado consigo mesmo (`_item_datas`: criação ≤ modificação).
  metadado_datas: F('regra_de_consistencia', 'arquivo'),
  metadado_modificacao_no_ato: F('protocolo', 'arquivo'),
  metadado_origem: F('protocolo', 'arquivo'),
  metadado_salvamentos: F('protocolo', 'arquivo'),
  // Vínculo (exame_eletronico_encadeamento.py): o que o protocolo e o cadastro
  // dizem, procurado no texto do documento.
  peca_codigo_de_registro: F('protocolo', 'texto_do_documento'),
  peca_cpf: F('cadastro_do_caso', 'texto_do_documento'),
  peca_nome: F('protocolo', 'texto_do_documento'),
  peca_proposta: F('protocolo', 'texto_do_documento'),
  // Códigos lidos das páginas (exame_eletronico_codigos_lidos.py, E157.F2): o código
  // decodificado da imagem da página contra o declarado no protocolo (ou o impresso no
  // texto); os dois estados explícitos só têm o lado do arquivo — nada foi consultado.
  codigos_lidos: F('protocolo', 'arquivo'),
  codigos_consulta_ao_portal: F(null, 'arquivo'),
  codigos_de_barras: F(null, 'arquivo'),
  // Cronologia (exame_eletronico_jornada.py): cada âncora contra o instante do
  // ato do protocolo. O formato das datas é outra coisa (`_item_formato`): a
  // regra é uma leitura sustentada por todas as âncoras, e o apurado é o que a
  // resolução fez — quantas datas ambíguas do comprovante resolveu (pela
  // declaração do protocolo ou pelas âncoras) ou quantas âncoras discordaram.
  cronologia_formato_de_data: F('regra_de_consistencia', 'resolucao_das_datas'),
  cronologia_registro_da_assinatura: F('protocolo', 'comprovante'),
  cronologia_assinatura_embutida: F('protocolo', 'arquivo'),
  cronologia_custodia: F('protocolo', 'registro_de_recebimento'),
};

/** As fontes de um item, ou `null` para chave que o catálogo não conhece. */
export function fontesDoItem(chave: string): FontesDoItem | null {
  return FONTES_POR_ITEM[chave] ?? null;
}
